import React, {useEffect, useMemo, useState} from "react";
import axios from "axios";
import {FaGlobe} from "react-icons/fa";

export default function WebsitesPage({
                                         contextPath,
                                         title = "Website Listing",
                                         brandColor = "#25408f",
                                         height = 575
                                     }) {
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [q, setQ] = useState("");

    useEffect(() => {
        let alive = true;
        setLoading(true);
        const headers = {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
        };
        axios
            .get(`${contextPath}/websites`, {headers})
            .then((res) => {
                if (!alive) return;
                setSites(Array.isArray(res.data) ? res.data : []);
            })
            .catch((e) => {
                if (!alive) return;
                setErr(e?.response?.data?.message || e?.message || "Failed to load websites");
            })
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [contextPath]);

    const normalized = useMemo(() => {
        // filter by client-side search (contains)
        const filtered = (sites || []).filter((s) =>
            (s?.url || "").toLowerCase().includes(q.trim().toLowerCase())
        );

        // group by initial letter A-Z; others go under '#'
        const groups = {};
        const alpha = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
        [...alpha, "#"].forEach((k) => (groups[k] = []));

        filtered.forEach((w) => {
            const url = String(w?.url || "").trim();
            const first = url.charAt(0).toUpperCase();
            const key = /^[A-Z]$/.test(first) ? first : "#";
            groups[key].push(w);
        });

        Object.keys(groups).forEach((k) =>
            groups[k].sort((a, b) => (a.url || "").localeCompare(b.url || "", undefined, {sensitivity: "base"}))
        );

        return groups;
    }, [sites, q]);

    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
    const toHref = (raw) => {
        if (!raw) return "#";
        const trimmed = raw.trim();
        if (/^https?:\/\//i.test(trimmed)) return trimmed;
        // mimic legacy behavior: assume a domain and prefix http://www.
        return `http://www.${trimmed.replace(/^www\./i, "")}`;
    };

    return (
        <div className="w-full overflow-auto" style={{ height }}>
            <div className="flex items-center gap-2 mb-3">
                <FaGlobe style={{color: brandColor}} className="w-5 h-5" aria-hidden/>
                <h1 className="text-xl font-semibold" style={{color: brandColor}}>
                    {title}
                </h1>
            </div>

            {/* A–Z jump bar */}
            <div className="flex flex-wrap items-center gap-2 text-sm mb-3">
                {letters.map((L) => {
                    const count = normalized[L]?.length || 0;
                    const disabled = count === 0;
                    return (
                        <a
                            key={L}
                            href={disabled ? undefined : `#ref${L}`}
                            className={`px-2 py-1 rounded border ${
                                disabled
                                    ? "text-gray-400 border-gray-200 cursor-default"
                                    : "hover:bg-gray-50"
                            }`}
                            style={{borderColor: brandColor, color: disabled ? undefined : brandColor}}
                            onClick={(e) => disabled && e.preventDefault()}
                            aria-disabled={disabled}
                            title={disabled ? `No websites for ${L}` : `Jump to ${L}`}
                        >
                            {L}
                        </a>
                    );
                })}

                <div className="ml-auto">
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search domains…"
                        className="border rounded px-3 py-1 text-sm"
                        style={{borderColor: brandColor}}
                    />
                </div>
            </div>

            {loading && <p className="text-sm text-gray-600">Loading websites…</p>}
            {err && <p className="text-sm text-red-600">{err}</p>}

            {/* Grouped sections */}
            {letters.map((L) => {
                const list = normalized[L] || [];
                if (list.length === 0) return null;
                return (
                    <section key={L} id={`ref${L}`} className="mb-6 scroll-mt-16">
                        <div className="flex items-center gap-2">
                            <h2 className="text-lg font-semibold" style={{color: brandColor}}>
                                {L}
                            </h2>
                            <div className="flex-1 border-t"/>
                        </div>

                        {/* 3-column grid, responsive */}
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
                            {list.map((w) => (
                                <div key={w.id} className="truncate">
                                    <a
                                        href={toHref(w.url)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-medium underline-offset-2 hover:underline"
                                        style={{color: brandColor}}
                                        title={w.url}
                                    >
                                        {w.url}
                                    </a>
                                </div>
                            ))}
                        </div>
                    </section>
                );
            })}

            {(normalized["#"]?.length || 0) > 0 && (
                <section id="refSymbol" className="mb-6 scroll-mt-16">
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-semibold" style={{color: brandColor}}>
                            #
                        </h2>
                        <div className="flex-1 border-t"/>
                    </div>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
                        {normalized["#"].map((w) => (
                            <div key={w.id} className="truncate">
                                <a
                                    href={toHref(w.url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-medium underline-offset-2 hover:underline"
                                    style={{color: brandColor}}
                                    title={w.url}
                                >
                                    {w.url}
                                </a>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
